// Zahlungen: Eingang mit Zuordnungsvorschlag, Teilzahlung, Skonto, Überzahlung als Guthaben, Storno – und die
// Auszahlung an Lieferanten als Zahlungsvorschlag mit Zahlungsliste. Beträge der Eingabe sind immer positiv; die
// Richtung nennt `direction`. Überwiesen wird außerhalb (Online-Banking); Kontoauszüge ordnet der Bankabgleich zu (contract/bank.ts).
import { defineContract } from '@shared/contract'
import { zId } from '@shared/ids'
import { listInput, listOutput } from '@shared/list'
import { z } from 'zod'
import { PAYMENT_METHODS, zAmountCents, zAmountOrZeroCents, zDay, zNote, zPartnerRef, zReason } from './common'

const READ = 'finance.payment.read'
const RECORD = 'finance.payment.record'
const PAYABLE_READ = 'finance.supplierInvoice.read'
const PAYABLE_WRITE = 'finance.supplierInvoice.write'

export const PAYMENT_DIRECTIONS = ['incoming', 'outgoing'] as const

/** Zuordnung einer Zahlung zu einem offenen Posten. */
export const zAllocationInput = z.strictObject({
  openItemId: zId,
  /** Betrag aus der Zahlung in Cent; ohne Angabe so viel wie möglich */
  amountCents: zAmountOrZeroCents.optional(),
  /**
   * Skonto in Cent. Ohne Angabe gewährt das System das vereinbarte Skonto, wenn die Wertstellung in der Frist liegt
   * und die Zahlung den Rest deckt. 0 = kein Skonto. Ein Betrag nach Ablauf der Frist oder über dem vereinbarten
   * Satz braucht `discountReason`.
   */
  discountCents: zAmountOrZeroCents.optional(),
  discountReason: z.string().trim().max(300).optional(),
})

const zAllocationResult = z.object({
  openItemId: zId,
  number: z.string().nullable(),
  /** payment | write_off */
  kind: z.string(),
  /** zugeordneter Betrag in Cent (immer positiv) */
  amountCents: z.int(),
  discountCents: z.int(),
  /** offener Betrag des Postens danach */
  openCents: z.int(),
})

const zPaymentResult = z.object({
  paymentId: zId,
  allocations: z.array(zAllocationResult),
  /** nicht zugeordneter Rest der Zahlung in Cent (Guthaben) */
  unallocatedCents: z.int(),
  /** ein Satz für Vorschau und Rückmeldung */
  message: z.string(),
})

export const paymentSuggest = defineContract({
  name: 'finance.payment.suggest',
  title: 'Zuordnung einer Zahlung vorschlagen',
  description: 'Schlägt zu Betrag und Verwendungszweck die passenden offenen Posten vor – mit Skonto, wo die Frist noch läuft.',
  llm: {
    description:
      'Schlägt vor, welchen offenen Posten ein Zahlungseingang (oder eine Auszahlung) zuzuordnen ist: zuerst nach ' +
      'Belegnummern im Verwendungszweck, dann nach dem exakten Betrag (auch abzüglich Skonto), sonst die ältesten ' +
      'fälligen Posten des Partners. Liefert je Posten den Betrag aus der Zahlung, das Skonto in der Frist und den Rest ' +
      'sowie den nicht zugeordneten Rest der Zahlung. Verändert nichts – gebucht wird mit finance.payment.record. Ohne ' +
      '`partnerId` sucht der Vorschlag den Kunden über die Belegnummer im Verwendungszweck.',
    keywords: ['Zahlung zuordnen', 'Zahlungseingang', 'Verwendungszweck', 'welche Rechnung', 'Zuordnungsvorschlag'],
  },
  input: z.strictObject({
    direction: z.enum(PAYMENT_DIRECTIONS).default('incoming'),
    partnerId: zId.optional(),
    amountCents: zAmountCents,
    reference: z.string().trim().max(300).optional(),
    /** Wertstellung – maßgeblich für die Skontofrist; Standard: heute */
    valueDate: zDay.optional(),
  }),
  output: z.object({
    partner: zPartnerRef.nullable(),
    valueDate: z.string(),
    allocations: z.array(
      z.object({
        openItemId: zId,
        number: z.string().nullable(),
        documentDate: z.string().nullable(),
        dueDate: z.string().nullable(),
        openCents: z.int(),
        amountCents: z.int(),
        discountCents: z.int(),
        discountUntil: z.string().nullable(),
        restCents: z.int(),
        /** reference | amount | oldest */
        matchedBy: z.string(),
      }),
    ),
    /** alle offenen Posten des Partners in Zahlungsrichtung – zum Umwählen */
    openItems: z.array(
      z.object({
        openItemId: zId,
        number: z.string().nullable(),
        typeTitle: z.string(),
        documentDate: z.string().nullable(),
        dueDate: z.string().nullable(),
        daysOverdue: z.int(),
        openCents: z.int(),
        discountUntil: z.string().nullable(),
        discountBp: z.int(),
        /** Skonto, das bei dieser Wertstellung zulässig ist */
        discountCents: z.int(),
      }),
    ),
    /** Guthaben des Partners (Rechnungskorrekturen, Leergutgutschriften), das sich verrechnen ließe */
    creditCents: z.int(),
    unallocatedCents: z.int(),
    hints: z.array(z.string()),
  }),
  permission: READ,
  risk: 'read',
  scope: 'any',
})

export const paymentRecord = defineContract({
  name: 'finance.payment.record',
  title: 'Zahlung buchen',
  description: 'Bucht einen Zahlungseingang oder eine Auszahlung und ordnet sie offenen Posten zu – mit Skonto in der Frist, Teilzahlung oder Guthaben.',
  llm: {
    description:
      'Bucht EINE Zahlung eines Partners: `direction: "incoming"` (Standard) ist ein Zahlungseingang vom Kunden, ' +
      '`"outgoing"` eine Auszahlung (an den Lieferanten oder als Erstattung). `amountCents` ist der Zahlbetrag in Cent ' +
      '(positiv), `method` die Zahlart (transfer = Überweisung, cash = bar – dann mit `locationId` der Filiale oder des ' +
      'Fahrzeugs, das kassiert hat), `valueDate` die Wertstellung. `allocations` ordnet Posten zu: ohne `amountCents` so ' +
      'viel wie möglich; Skonto gewährt das System in der Frist von selbst, nach der Frist nur mit `discountReason`. ' +
      'Was nicht zugeordnet ist, bleibt als Guthaben an der Zahlung. Den Vorschlag dazu liefert ' +
      'finance.payment.suggest. Nicht für Kassenverkäufe (Kasse) und nicht zum Verrechnen von Gutschriften ' +
      '(finance.openItem.offset).',
    keywords: ['Zahlung buchen', 'Zahlungseingang', 'bezahlt', 'Überweisung eingegangen', 'Skonto', 'Teilzahlung', 'Barzahlung Fahrer'],
  },
  input: z.strictObject({
    direction: z.enum(PAYMENT_DIRECTIONS).default('incoming'),
    partnerId: zId,
    amountCents: zAmountCents,
    method: z.enum(PAYMENT_METHODS, { error: 'Bitte die Zahlart wählen.' }),
    valueDate: zDay,
    /** Verwendungszweck bzw. Beleg des Zahlungswegs */
    reference: z.string().trim().max(140).nullish(),
    note: zNote,
    /** wo kassiert wurde (Filiale, Fahrzeug) – Pflicht bei Barzahlung */
    locationId: zId.nullish(),
    allocations: z.array(zAllocationInput).max(200).default([]),
  }),
  output: zPaymentResult,
  permission: RECORD,
  risk: 'financial',
  scope: (input) => (input.locationId ? [input.locationId] : 'tenant'),
  effects: { reversible: true },
  limits: { maxItems: 200, itemCount: (input) => input.allocations.length },
})

export const paymentAllocate = defineContract({
  name: 'finance.payment.allocate',
  title: 'Guthaben einer Zahlung zuordnen',
  description: 'Ordnet den nicht zugeordneten Rest einer gebuchten Zahlung weiteren offenen Posten zu.',
  llm: {
    description:
      'Ordnet den freien Rest einer bereits gebuchten Zahlung (Überzahlung, Anzahlung) offenen Posten desselben ' +
      'Partners zu. Skonto wie bei finance.payment.record. Verwenden, wenn ein Guthaben aus einer Zahlung gegen eine ' +
      'spätere Rechnung laufen soll. Guthaben aus Rechnungskorrekturen verrechnet finance.openItem.offset.',
    keywords: ['Guthaben zuordnen', 'Überzahlung verrechnen', 'Anzahlung anrechnen', 'nicht zugeordnete Zahlung'],
  },
  input: z.strictObject({ paymentId: zId, allocations: z.array(zAllocationInput).min(1, { error: 'Bitte mindestens einen Posten wählen.' }).max(200) }),
  output: zPaymentResult,
  permission: RECORD,
  risk: 'financial',
  scope: 'record',
})

export const paymentReverse = defineContract({
  name: 'finance.payment.reverse',
  title: 'Zahlung stornieren',
  description: 'Storniert eine Zahlung samt ihren Zuordnungen: Die Posten sind wieder offen, eine Gegenzahlung hält den Vorgang fest.',
  llm: {
    description:
      'Storniert eine gebuchte Zahlung (Fehlbuchung, Rücklastschrift): Alle Zuordnungen samt Skonto werden ' +
      'zurückgenommen, die Posten sind wieder offen, und eine Stornozahlung mit umgekehrtem Betrag entsteht. Braucht ' +
      'einen Grund. Nichts wird gelöscht. Nicht zum Ändern einer Zuordnung allein – dafür stornieren und neu buchen.',
    keywords: ['Zahlung stornieren', 'Rücklastschrift', 'Fehlbuchung', 'Zahlung zurücknehmen'],
  },
  input: z.strictObject({ paymentId: zId, reason: zReason }),
  output: z.object({ paymentId: zId, reversalId: zId, reopened: z.array(z.object({ openItemId: zId, number: z.string().nullable(), openCents: z.int() })), message: z.string() }),
  permission: RECORD,
  risk: 'destructive',
  scope: 'record',
})

export const PAYMENT_SORT_KEYS = ['valueDate', 'amountCents', 'partnerName'] as const

export const zPaymentRow = z.object({
  id: zId,
  valueDate: z.string(),
  businessDate: z.string(),
  direction: z.enum(PAYMENT_DIRECTIONS),
  partner: zPartnerRef.nullable(),
  method: z.string(),
  methodLabel: z.string(),
  /** Zahlbetrag in Cent (positiv; Stornozahlungen negativ) */
  amountCents: z.int(),
  allocatedCents: z.int(),
  unallocatedCents: z.int(),
  discountCents: z.int(),
  reference: z.string().nullable(),
  note: z.string().nullable(),
  locationCode: z.string().nullable(),
  /** storniert (es gibt eine Stornozahlung) */
  reversed: z.boolean(),
  /** diese Zeile IST die Stornozahlung zu … */
  reversalOfId: zId.nullable(),
  /** active | reversed | reversal */
  state: z.string(),
  postedByName: z.string().nullable(),
  /** Belegnummern der zugeordneten Posten */
  numbers: z.array(z.string()),
})

export const paymentList = defineContract({
  name: 'finance.payment.list',
  title: 'Zahlungen anzeigen',
  description: 'Gebuchte Zahlungen mit Partner, Zahlart, Zuordnung und nicht zugeordnetem Rest.',
  llm: {
    description:
      'Listet gebuchte Zahlungen: Wertstellung, Partner, Zahlart, Betrag in Cent, zugeordneter und nicht zugeordneter ' +
      'Teil, Skonto, Verwendungszweck und die Belegnummern der ausgeglichenen Posten. Filter: Richtung (incoming, ' +
      'outgoing), Partner, Zahlart, Zeitraum, nur mit nicht zugeordnetem Rest. `q` sucht in Verwendungszweck und ' +
      'Partnername. Einzelheiten einer Zahlung: finance.payment.get.',
    keywords: ['Zahlungen', 'Zahlungseingänge', 'Auszahlungen', 'nicht zugeordnet', 'Guthaben', 'Kontobewegungen'],
  },
  input: listInput({
    sortBy: PAYMENT_SORT_KEYS,
    defaultSort: { by: 'valueDate', dir: 'desc' },
    filters: {
      direction: z.enum(PAYMENT_DIRECTIONS).optional(),
      partnerId: zId.optional(),
      method: z.enum(PAYMENT_METHODS).optional(),
      from: zDay.optional(),
      to: zDay.optional(),
      unallocatedOnly: z.boolean().optional(),
    },
  }),
  output: listOutput(zPaymentRow),
  permission: READ,
  risk: 'read',
  scope: 'any',
})

export const paymentGet = defineContract({
  name: 'finance.payment.get',
  title: 'Zahlung anzeigen',
  description: 'Eine Zahlung mit jeder Zuordnung, Skonto und Storno.',
  llm: {
    description:
      'Liefert eine Zahlung vollständig: Kopf wie in finance.payment.list und jede Zuordnung mit Beleg, Betrag, Skonto ' +
      'und ob sie zurückgenommen wurde. Verwenden, wenn die Kennung der Zahlung bekannt ist.',
    keywords: ['Zahlung', 'Zuordnung', 'welche Rechnung wurde bezahlt'],
  },
  input: z.strictObject({ id: zId }),
  output: zPaymentRow.extend({
    allocations: z.array(
      z.object({
        id: zId,
        openItemId: zId,
        documentId: zId.nullable(),
        documentType: z.string().nullable(),
        number: z.string().nullable(),
        kind: z.string(),
        amountCents: z.int(),
        discountCents: z.int(),
        note: z.string().nullable(),
        reversed: z.boolean(),
      }),
    ),
    canReverse: z.boolean(),
  }),
  permission: READ,
  risk: 'read',
  scope: 'record',
})

// ---- Auszahlung an Lieferanten ---------------------------------------------------------------------------------

export const zPayableRow = z.object({
  openItemId: zId,
  supplier: zPartnerRef,
  documentId: zId.nullable(),
  documentType: z.string().nullable(),
  typeTitle: z.string(),
  number: z.string().nullable(),
  /** Rechnungsnummer des Lieferanten */
  supplierInvoiceNo: z.string().nullable(),
  documentDate: z.string().nullable(),
  dueDate: z.string().nullable(),
  /** Tage bis zur Fälligkeit am Zahltag (negativ = überfällig) */
  dueInDays: z.int().nullable(),
  /** Rechnung +, Gutschrift des Lieferanten − */
  openCents: z.int(),
  discountUntil: z.string().nullable(),
  discountBp: z.int(),
  /** Skonto, das am Zahltag noch gezogen werden kann */
  discountCents: z.int(),
  /** zu überweisen: offen abzüglich Skonto (Gutschriften negativ) */
  payCents: z.int(),
  /** vorgeschlagen: fällig bis zum Stichtag oder Skontofrist läuft bis dahin ab */
  recommended: z.boolean(),
  /** „fällig seit 3 Tagen“, „Skonto bis 21.10. (30,60 €)“ */
  reason: z.string(),
})

export const payableProposal = defineContract({
  name: 'finance.payable.proposal',
  title: 'Zahlungsvorschlag für Lieferanten anzeigen',
  description: 'Freigegebene Eingangsrechnungen nach Fälligkeit und Skontofrist – mit dem Vorschlag, was jetzt zu zahlen ist.',
  llm: {
    description:
      'Zahlungsvorschlag: offene, freigegebene Eingangsrechnungen und Gutschriften der Lieferanten mit Fälligkeit, ' +
      'Skontofrist, Skontobetrag und dem zu überweisenden Betrag in Cent. Vorgeschlagen (`recommended`) ist, was bis ' +
      '`dueUntil` fällig wird oder dessen Skontofrist bis dahin abläuft. Verändert nichts. Verwenden für „Was müssen ' +
      'wir diese Woche zahlen?“. Bezahlt gebucht wird mit finance.payable.pay (oder beim Bankabgleich). Überwiesen wird außerhalb.',
    keywords: ['Zahlungsvorschlag', 'Zahllauf', 'Lieferanten bezahlen', 'fällige Rechnungen', 'Skonto ziehen', 'Verbindlichkeiten'],
  },
  input: z.strictObject({
    supplierId: zId.optional(),
    /** Tag der Überweisung; Standard: heute */
    payDate: zDay.optional(),
    /** vorgeschlagen wird, was bis zu diesem Tag fällig ist; Standard: Zahltag + 7 Tage */
    dueUntil: zDay.optional(),
  }),
  output: z.object({
    payDate: z.string(),
    dueUntil: z.string(),
    rows: z.array(zPayableRow),
    totals: z.object({ count: z.int(), openCents: z.int(), recommendedCount: z.int(), recommendedCents: z.int(), discountCents: z.int() }),
    notice: z.string(),
  }),
  permission: PAYABLE_READ,
  risk: 'read',
})

export const payablePaymentList = defineContract({
  name: 'finance.payable.paymentList',
  title: 'Zahlungsliste als Datei erzeugen',
  description: 'Die gewählten Eingangsrechnungen als CSV-Datei für das Online-Banking: Empfänger, Verwendungszweck, Betrag.',
  llm: {
    description:
      'Erzeugt die Zahlungsliste der gewählten Posten als CSV-Text: je Lieferant eine Zeile mit Empfänger, ' +
      'Verwendungszweck (Rechnungsnummern des Lieferanten), Betrag nach Skonto und abgezogenen Gutschriften. Bucht ' +
      'nichts und überweist nichts – es gibt keine Verbindung zur Bank. Danach finance.payable.pay, sobald überwiesen ist.',
    keywords: ['Zahlungsliste', 'Überweisungsliste', 'CSV', 'Online-Banking', 'Zahllauf exportieren'],
  },
  input: z.strictObject({ payDate: zDay.optional(), openItemIds: z.array(zId).min(1, { error: 'Bitte mindestens einen Posten wählen.' }).max(200) }),
  output: z.object({ fileName: z.string(), csv: z.string(), count: z.int(), totalCents: z.int() }),
  permission: PAYABLE_READ,
  risk: 'read',
  limits: { maxItems: 200, itemCount: (input) => input.openItemIds.length },
})

export const payablePay = defineContract({
  name: 'finance.payable.pay',
  title: 'Eingangsrechnungen als bezahlt buchen',
  description: 'Bucht je Lieferant eine Auszahlung über die gewählten Rechnungen – Skonto in der Frist, Gutschriften werden verrechnet.',
  llm: {
    description:
      'Bucht die gewählten Eingangsrechnungen als bezahlt: je Lieferant EINE Auszahlung über die Summe; Skonto wird ' +
      'abgezogen, wenn `valueDate` in der Skontofrist liegt; gewählte Gutschriften des Lieferanten werden zuerst ' +
      'verrechnet. Verwenden, NACHDEM überwiesen wurde – das System überweist nichts selbst. Einzelne Auszahlung mit ' +
      'abweichendem Betrag: finance.payment.record mit direction "outgoing".',
    keywords: ['als bezahlt buchen', 'Lieferant bezahlt', 'Zahllauf buchen', 'Auszahlung'],
  },
  input: z.strictObject({
    valueDate: zDay,
    method: z.enum(PAYMENT_METHODS).default('transfer'),
    reference: z.string().trim().max(140).nullish(),
    openItemIds: z.array(zId).min(1, { error: 'Bitte mindestens einen Posten wählen.' }).max(200),
  }),
  output: z.object({
    payments: z.array(z.object({ paymentId: zId.nullable(), supplier: zPartnerRef, amountCents: z.int(), discountCents: z.int(), offsetCents: z.int(), itemCount: z.int() })),
    totalCents: z.int(),
    message: z.string(),
  }),
  permission: PAYABLE_WRITE,
  risk: 'financial',
  limits: { maxItems: 200, itemCount: (input) => input.openItemIds.length },
})
